import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractorSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        
        if (response.data.status === "expired" || attempts >= maxAttempts) {
          setStatus("error");
          return;
        }

        // Continue polling
        setAttempts((prev) => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      } catch (error) {
        console.error("Error checking payment:", error);
        if (attempts >= maxAttempts) {
          setStatus("error");
        } else {
          setAttempts((prev) => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-white" data-testid="contractor-success-page">
      <Navbar />

      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {status === "loading" && (
            <div data-testid="payment-loading">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-slate-400 animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">
                Vérification du paiement...
              </h1>
              <p className="text-slate-600">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
            </div>
          )}

          {status === "success" && (
            <div data-testid="payment-success">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-3">
                Bienvenue dans notre réseau !
              </h1>
              <p className="text-slate-600 mb-8">
                Votre compte entrepreneur a été créé avec succès. Vous commencerez à recevoir des demandes de devis très bientôt.
              </p>
              <div className="bg-slate-50 rounded-xl p-6 mb-8 text-left">
                <h3 className="font-semibold text-slate-900 mb-4">Prochaines étapes :</h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Vous recevrez un email de confirmation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Les demandes de devis vous seront envoyées par email</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Contactez les clients directement pour proposer vos services</span>
                  </li>
                </ul>
              </div>
              <Link to="/">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          )}

          {status === "error" && (
            <div data-testid="payment-error">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">
                Paiement non confirmé
              </h1>
              <p className="text-slate-600 mb-8">
                Nous n'avons pas pu confirmer votre paiement. Si vous pensez qu'il s'agit d'une erreur, veuillez nous contacter.
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/entrepreneur">
                  <Button variant="outline">
                    Réessayer
                  </Button>
                </Link>
                <Link to="/">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
